import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

const EMAIL = /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/;

/** 「名前 <a@b>」や区切り文字まじりの貼り付けからアドレスだけを取り出す */
export const parseAddresses = (value: string): string[] => {
  const found = value.match(/[^\s<>"',;、，；]+@[^\s<>"',;、，；]+/g);
  // アドレスの形をしていない入力もチップにして、誤りを目で見て直せるようにする
  if (!found) return value.split(/[,;、，；\s]+/).map((v) => v.trim().toLowerCase()).filter(Boolean);
  return found.map((email) => email.toLowerCase());
};

/** 宛先の入力。Enter・カンマ・フォーカスを外したときにチップにする（Gmail と同じ） */
export const RecipientInput = ({
  id,
  label,
  value,
  onChange,
  autoFocus,
  trailing,
}: {
  id: string;
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  autoFocus?: boolean;
  /** 行の右端に置く操作（Cc / Bcc の表示切り替え） */
  trailing?: ReactNode;
}) => {
  const [draft, setDraft] = useState('');

  const commit = (text: string) => {
    const added = parseAddresses(text);
    if (added.length === 0) return;
    onChange([...new Set([...value, ...added])]);
    setDraft('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (['Enter', ',', ';', 'Tab'].includes(event.key) && draft.trim()) {
      event.preventDefault();
      commit(draft);
    } else if (event.key === 'Backspace' && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="flex min-h-touch items-start gap-2 border-b border-border py-1.5 lg:min-h-10">
      <label htmlFor={id} className="w-10 shrink-0 pt-1.5 text-body text-foreground-muted">
        {label}
      </label>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
        {value.map((email) => (
          <span
            key={email}
            className={cn(
              'inline-flex max-w-full items-center gap-1 rounded-control border px-2 py-0.5 text-note',
              EMAIL.test(email) ? 'border-border bg-muted' : 'border-2 border-foreground font-semibold',
            )}
            title={EMAIL.test(email) ? email : 'メールアドレスの形式が正しくありません'}
          >
            <span className="truncate">{email}</span>
            <button
              type="button"
              onClick={() => onChange(value.filter((v) => v !== email))}
              className="rounded-control p-0.5 hover:bg-border"
              aria-label={`${email} を外す`}
            >
              <X aria-hidden className="size-3.5" />
            </button>
          </span>
        ))}
        <input
          id={id}
          type="email"
          multiple
          autoComplete="email"
          autoFocus={autoFocus}
          value={draft}
          onChange={(e) => {
            const next = e.target.value;
            // 貼り付けなどで区切り文字が入ったらその場でチップにする
            if (/[,;、，；]/.test(next)) commit(next);
            else setDraft(next);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => commit(draft)}
          className="min-w-32 flex-1 bg-transparent py-1 text-input outline-none lg:text-body"
        />
      </div>
      {trailing && <div className="flex shrink-0 gap-1 pt-1.5 text-note">{trailing}</div>}
    </div>
  );
};

export const hasInvalidAddress = (list: string[]): boolean => list.some((email) => !EMAIL.test(email));
